/**
 * CommandPalette.jsx — Búsqueda/navegación global (Ctrl/⌘ K)
 * -----------------------------------------------------------------------------
 * Fase 0.1 del roadmap BPM (docs/bpm-erp-roadmap.md). 100% front: usa los menús
 * ya cargados (flujos, tareas, páginas, Menus Personalizado, admin) que le pasa
 * MainLayout. Patrón estándar de las suites modernas.
 *
 * Se abre con Ctrl/⌘ K o con el evento `xflow:open-command-palette` (lo dispara
 * el botón de búsqueda del header). Navegación por teclado + recientes.
 */
import React from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

const RECENTS_KEY = "xflow-cmdk-recents";
const MAX_RESULTS = 30;

/* Coincidencia: substring (prioritaria) o subsecuencia difusa. Devuelve score. */
function score(haystack, term) {
  const h = haystack.toLowerCase();
  const idx = h.indexOf(term);
  if (idx >= 0) return 1000 - idx; // match contiguo: mejor cuanto más al inicio
  let ti = 0;
  let s = 0;
  for (const c of term) {
    const f = h.indexOf(c, ti);
    if (f < 0) return 0;
    s += 1;
    ti = f + 1;
  }
  return s;
}

export default function CommandPalette({ items = [] }) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [idx, setIdx] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  /* Abrir/cerrar con teclado + evento del header */
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("xflow:open-command-palette", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("xflow:open-command-palette", onOpen);
    };
  }, []);

  React.useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const recents = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
    } catch (_) {
      return [];
    }
  }, [open]);

  const results = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) {
      const recentItems = recents
        .map((id) => items.find((i) => i.id === id))
        .filter(Boolean);
      const rest = items.filter((i) => !recents.includes(i.id));
      return [...recentItems, ...rest].slice(0, MAX_RESULTS);
    }
    return items
      .map((i) => ({
        i,
        s: score(`${i.label} ${i.group || ""} ${i.sublabel || ""}`, term),
      }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, MAX_RESULTS)
      .map((x) => x.i);
  }, [q, items, recents]);

  React.useEffect(() => setIdx(0), [q]);

  /* mantener el item activo visible */
  React.useEffect(() => {
    const el = listRef.current?.querySelector(".xcmdk-item--active");
    el?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  const select = (item) => {
    if (!item) return;
    try {
      const next = [item.id, ...recents.filter((id) => id !== item.id)].slice(0, 6);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch (_) {
      /* ignore */
    }
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(results[idx]);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="xcmdk-overlay" onClick={() => setOpen(false)}>
      <div
        className="xcmdk-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Búsqueda rápida"
      >
        <div className="xcmdk-search">
          <Search size={18} className="xcmdk-search-icon" />
          <input
            ref={inputRef}
            className="xcmdk-input"
            placeholder="Buscar flujos, tareas, páginas…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            autoComplete="off"
          />
          <kbd className="xcmdk-kbd">ESC</kbd>
        </div>

        <div className="xcmdk-results" ref={listRef}>
          {results.length === 0 ? (
            <div className="xcmdk-empty">Sin resultados</div>
          ) : (
            results.map((item, i) => (
              <button
                key={item.id}
                type="button"
                className={"xcmdk-item" + (i === idx ? " xcmdk-item--active" : "")}
                onMouseEnter={() => setIdx(i)}
                onClick={() => select(item)}
              >
                <span className={"xcmdk-item-icon e-icons " + (item.icon || "e-group-2")}></span>
                <span className="xcmdk-item-text">
                  <span className="xcmdk-item-label">{item.label}</span>
                  {item.group && <span className="xcmdk-item-group">{item.group}</span>}
                </span>
                {i === idx && <span className="xcmdk-item-enter">↵</span>}
              </button>
            ))
          )}
        </div>

        <div className="xcmdk-footer">
          <span><kbd className="xcmdk-kbd">↑</kbd><kbd className="xcmdk-kbd">↓</kbd> navegar</span>
          <span><kbd className="xcmdk-kbd">↵</kbd> abrir</span>
          <span><kbd className="xcmdk-kbd">Ctrl/⌘ K</kbd> alternar</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
